import z from "zod";
import { mapErrorsToFields } from "../../../../utils";
import CharityEventTypeService from "../../../../services/CharityEventTypeService";

const api = new CharityEventTypeService();

export type State = {
  message: string | null;
  errors?: {
    name?: string[];
    general?: string[];
  };
  success: boolean;
  defaultValues: {
    name?: string;
  };
};

const FormSchema = z.object({
  name: z.string().min(1, { message: "Name is required." }),
});

export async function createCharityEventType(
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = {
    name: formData.get("name")?.toString() || "",
  };
  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to create charity event type.",
        defaultValues,
        success: false,
      };
    }
    const result = await api.createCharityEventType(validatedFields.data);
    if (result.status === 201)
      return {
        errors: {},
        message: "Charity event type created successfully.",
        defaultValues,
        success: true,
      };
    else if (result.status === 400 || result.status === 409)
      return {
        errors: {},
        message: result.data.description,
        defaultValues,
        success: false,
      };
  } catch (error) {
    console.error("Error creating charity event type:", error);
  }
  return {
    errors: {},
    message: "Failed to create charity event type.",
    defaultValues,
    success: false,
  };
}

export async function updateCharityEventType(
  id: number,
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = {
    name: formData.get("name")?.toString() || "",
  };
  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to update charity event type.",
        defaultValues,
        success: false,
      };
    }
    const result = await api.updateCharityEventType({
      id: id,
      ...validatedFields.data,
    });
    if (result.status === 204)
      return {
        errors: {},
        message: "Charity event type updated successfully.",
        defaultValues,
        success: true,
      };
  } catch (error) {
    console.error("Error updating charity event type:", error);
  }
  return {
    errors: {},
    message: "Failed to update charity event type.",
    defaultValues,
    success: false,
  };
}

export async function deleteCharityEventType(
  id: number
): Promise<{ success: boolean }> {
  try {
    const result = await api.deleteCharityEventType(id);
    return { success: result.status === 204 };
  } catch (error) {
    console.error("Database Error:", error);
  }
  return { success: false };
}
